"use client";
import Image from "next/image";
import { SectionHeader } from "./SectionHeader";
import { useState } from "react";
import type { ReactNode } from "react";

interface CardProps {
  title: string;
  subtitle: string;
  imageSrc?: string;
  imageAlt?: string;
  className?: string;
  details?: string;
  preventPopup?: boolean;
  chart?: ReactNode;
}

export function Card({ title, subtitle, imageSrc, imageAlt, className = "", details, preventPopup = false, chart }: CardProps): JSX.Element {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openPopup = (event: React.MouseEvent): void => {
    event.preventDefault();
    if (!preventPopup) {
      setIsPopupOpen(true);
    }
  };
  const closePopup = (): void => setIsPopupOpen(false);

  return (
    <>
      <div 
        className={`bg-white/5 rounded-lg p-1 transition-shadow ${preventPopup ? 'cursor-default' : 'cursor-pointer hover:shadow-xl'} ${className}`}
        onClick={openPopup}
      >
        {chart ? (
          <div className="mb-4">
            {chart}
          </div>
        ) : imageSrc && (
          <div className="mb-4 relative w-full h-48 overflow-hidden rounded-t-lg">
            <Image 
              src={imageSrc} 
              alt={imageAlt ?? title} 
              fill 
              className="object-cover object-top w-full h-full"
            />
          </div>
        )}
        <div className="p-2">
          <SectionHeader variant="subtle" size="md" className="mb-4">
            {title}
          </SectionHeader>
          <p className="mb-4">{subtitle}</p>
        </div>
      </div>

      {/* Popup/Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closePopup}>
          <div 
            className="bg-white dark:bg-gray-700 text-black dark:text-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto relative"
            onClick={(e: React.MouseEvent) => e.stopPropagation()} // Prevent closing when clicking inside
          >
  
            {imageSrc && (
              <div className="mb-6 relative w-full h-56 overflow-hidden rounded-md">
                <Image 
                  src={imageSrc} 
                  alt={imageAlt ?? title} 
                  fill 
                  className="object-contain w-full h-full"
                />
              </div>
            )}
            <SectionHeader variant='subtle' className="mb-2">
              {title}
            </SectionHeader>
            <p className="mb-6 text-lg font-medium">{subtitle}</p>
            
            {details ? (
              <div className="prose max-w-none">
                <p>{details}</p>
              </div>
            ) : (
              <p className="text-gray-400 italic">No additional details available.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
} 